<script setup lang="ts">
import { onMounted, ref } from "vue";
import { Resources, ResourceType } from "../../interface/ResourcesInterface";
import { StorageService } from "../../services/storage.service";
import { StorageKeys } from "../../services/storage.service";
import { appContainer } from "../../container";
import DeleteResourceModal from "./DeleteResourceModal.vue";
import { ActiveRolesStore } from "../../stores/ActiveRolesStore";
import { Roles } from "../../interface/UserInterface";
import YtEmbedModal from "./YtEmbedModal.vue";

const props = defineProps<{
  resource: Resources;
}>();

const storageService = appContainer.resolve(StorageService);

const token = ref<string>("");
const resourceId = ref();
const showDeleteModal = ref(false);
const showEmbedModal = ref(false);
const activeRoleStore = ActiveRolesStore();
const resourceTypes = ResourceType;

const emit = defineEmits(["ResourceDeleted"]);

const formatDate = (dateString: Date | string) => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  return date.toLocaleDateString("en-US", options);
};

function emitDelete() {
  showDeleteModal.value = false;
  emit("ResourceDeleted");
}

function deleteResource() {
  resourceId.value = props.resource.id;
  showDeleteModal.value = true;
}

function openEmbbedModal() {
  showEmbedModal.value = true;
}

function visibleTo(roles: string[]) {
  return activeRoleStore.activeRoles?.some((item) => roles.includes(item));
}

function handleResourceClick() {
  if (props.resource.type === resourceTypes.YTVideo.toLowerCase()) {
    openEmbbedModal();
  } else {
    const fileUrl = `${props.resource.file}?token=${token.value}`;
    window.open(fileUrl, "_blank");
  }
}

onMounted(async () => {
  token.value = (
    (await storageService.get(StorageKeys.TOKEN)) as any
  ).access_token;
});
</script>
<template>
  <div
    class="listing-company d-flex align-items-center w-100 py-3 border-bottom"
  >
    <div class="d-flex align-items-center w-100" @click="handleResourceClick">
      <div class="img-resource rounded overflow-hidden">
        <template
          v-if="props.resource.type === resourceTypes.Pdf.toLowerCase()"
        >
          <i class="bi bi-filetype-pdf"></i>
        </template>
        <template
          v-if="props.resource.type === resourceTypes.YTVideo.toLowerCase()"
        >
          <i class="bi bi-youtube"></i>
        </template>
      </div>
      <div class="company-info px-2 mx-2">
        <h6 class="ff-montserrat fs-medium text-capitalize mb-1">
          {{ props.resource.name }}
        </h6>
        <p
          v-if="props.resource.description"
          class="description size-sm mb-1 text-body-secondary"
        >
          {{ props.resource.description }}
        </p>
        <div class="d-flex gap-2 size-sm">
          <p class="mb-0">
            <span>Uploaded on: </span>
            <strong class="fw-medium"
              >{{ formatDate(props.resource.created_at) }}
            </strong>
          </p>
        </div>
      </div>
    </div>
    <div class="group-btns my-auto ms-auto flex-column flex-sm-row gap-2">
      <button
        class="border-0 bg-transparent"
        @click="deleteResource()"
        v-if="visibleTo([Roles.SuperAdmin])"
      >
        <i class="bi bi-trash3"></i>
      </button>
      <button
        class="border-0 bg-transparent"
        v-if="props.resource.type === ResourceType.Pdf.toLowerCase()"
      >
        <a
          target="_blank"
          :href="`${props.resource.file}?token=${token}`"
          class="btn border-0 bg-transparent p-0"
        >
          <i class="bi bi-download"></i>
        </a>
      </button>
      <button v-else class="border-0 bg-transparent" @click="openEmbbedModal">
        <i class="bi bi-play-fill"></i>
      </button>
    </div>
  </div>
  <YtEmbedModal
    v-if="showEmbedModal"
    @close="showEmbedModal = false"
    :resource="props.resource"
  />
  <DeleteResourceModal
    v-if="showDeleteModal"
    :resourceId="resourceId"
    @delete="emitDelete"
    @close="showDeleteModal = false"
  />
</template>

<style lang="scss" scoped>
.size-sm {
  font-size: 12px;
}
.description {
  word-break: break-word;
  white-space: pre-line;
}
.group-btns {
  display: flex;
  align-items: center;
  z-index: 2;
  position: relative;
  a {
    font-size: 18px;
    color: #000;
  }

  button {
    font-size: 18px;
    color: #000;
    &:nth-child(1):hover,
    &:nth-child(2):hover,
    &:nth-child(3):hover {
      color: #4a84dd;
    }

    &:nth-child(4):hover {
      color: red;
    }
  }
}
</style>
